import React, { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { customerRepo, dashboardRepo, documentRepo, chatThreadRepo, ORDER_TYPE } from "../../services";
import { settleInvoicePayment } from "../../services/settlement";
import { useApp } from "../../context/AppContext";
import { useI18n } from "../../i18n";
import PaymentQrModal from "../../components/PaymentQrModal";
import BuyerTaxFields, { buyerTaxError, buyerTaxOf, cleanBuyerTax } from "../../components/BuyerTaxFields";
import {
  Avatar, Badge, Button, Card, DataTable, EmptyState, Modal, PageHead, ProgressBar, Skeleton, StatusBadge, Tabs,
  Timeline, baht, formatDate, useAsync,
} from "../../components/ui";
import Icon from "../../components/ui/Icon";

export default function Customer360() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { orgId, organization, hasModule, toast } = useApp();
  const etax = hasModule("etax");
  const { tx } = useI18n();
  const [tab, setTab] = useState("overview");
  const [payInvoice, setPayInvoice] = useState(null);
  const [taxOpen, setTaxOpen] = useState(false);
  const [taxForm, setTaxForm] = useState(null);

  const { data: customer, loading, reload: reloadCustomer } = useAsync(() => customerRepo.get(orgId, id), [orgId, id]);
  const { data: summary, reload } = useAsync(() => dashboardRepo.customerSummary(orgId, id), [orgId, id]);
  const { data: documents } = useAsync(() => documentRepo.all(orgId, (row) => row.customerId === id), [orgId, id], []);
  const { data: threads } = useAsync(() => chatThreadRepo.all(orgId, (row) => row.customerId === id), [orgId, id], []);

  if (loading || !customer) return <Skeleton height={420} />;

  // ไปหน้าแชทของลูกค้ารายนี้ — ถ้ายังไม่มี thread ในพอร์ทัลให้สร้างก่อน
  const openChat = async () => {
    try {
      const existing = (threads || []).find((t) => t.channel === "portal") || (threads || [])[0];
      const thread =
        existing ||
        (await chatThreadRepo.create(orgId, {
          customerId: customer.id,
          customerName: customer.name,
          channel: "portal",
          lastMessage: "",
          unread: 0,
          status: "open",
          updatedAt: new Date().toISOString(),
        }));
      navigate(`/admin/chat?thread=${thread.id}`);
    } catch (err) {
      toast(err.message, "error");
    }
  };

  const saveTax = async () => {
    const message = buyerTaxError(taxForm);
    if (message) return toast(message, "error");
    await customerRepo.update(orgId, customer.id, cleanBuyerTax(taxForm));
    setTaxOpen(false);
    reloadCustomer();
    toast(tx("บันทึกข้อมูลภาษีของลูกค้าแล้ว (ใบกำกับภาษีที่ออกไปแล้วไม่เปลี่ยน)", "Tax details saved"), "success");
  };

  const tabs = [
    { key: "overview", label: tx("ภาพรวม", "Overview") },
    { key: "appointments", label: tx("นัดหมาย", "Appointments"), count: summary?.appointments?.length },
    { key: "billing", label: tx("บิล & การชำระ", "Billing"), count: summary?.invoices?.length },
    { key: "packages", label: tx("คอร์ส/แพ็กเกจ", "Packages"), count: summary?.packages?.length },
    { key: "contracts", label: tx("สัญญา", "Contracts"), count: summary?.contracts?.length },
    { key: "documents", label: tx("เอกสาร", "Documents"), count: documents?.length },
    { key: "activity", label: tx("ไทม์ไลน์", "Timeline") },
  ];

  return (
    <>
      <PageHead
        title={customer.name}
        subtitle={`${customer.code} · ${customer.phone} · ${customer.email}`}
        actions={
          <>
            <Button onClick={() => navigate("/admin/customers")}>← {tx("กลับ", "Back")}</Button>
            <Button variant="soft" onClick={openChat}><Icon name="chat" /> {tx("แชท", "Chat")}</Button>
            <Link className="btn-crm btn-primary-crm" to="/admin/billing">+ {tx("สร้างเอกสาร", "New document")}</Link>
          </>
        }
      />

      <div className="grid-sidebar">
        <div>
          <Card>
            <div className="u-center u-mb-16">
              <Avatar name={customer.name} size="lg" />
              <div className="u-strong u-fs-18 u-mt-8">{customer.name}</div>
              <div className="u-fs-13 u-muted">{customer.nickname ? `(${customer.nickname})` : ""}</div>
              <div className="u-mt-8">
                <Badge tone="violet">{customer.tier}</Badge>{" "}
                <StatusBadge status={customer.isActive ? "active" : "inactive"} />
              </div>
            </div>

            <div className="u-fs-13" style={{ display: "grid", gap: 8 }}>
              <div className="u-between"><span className="u-muted">{tx("เบอร์โทร", "Phone")}</span><span>{customer.phone}</span></div>
              <div className="u-between"><span className="u-muted">{tx("อีเมล", "Email")}</span><span>{customer.email}</span></div>
              <div className="u-between"><span className="u-muted">LINE</span><span>{customer.lineId || "-"}</span></div>
              <div className="u-between"><span className="u-muted">{tx("ช่องทาง", "Channel")}</span><span>{customer.channel}</span></div>
              <div className="u-between"><span className="u-muted">{tx("วันเกิด", "Birthday")}</span><span>{formatDate(customer.birthDate)}</span></div>
              <div className="u-between"><span className="u-muted">{tx("มาล่าสุด", "Last visit")}</span><span>{formatDate(customer.lastVisitAt)}</span></div>
              <div><span className="u-muted">{tx("ที่อยู่", "Address")}</span><div>{customer.address} {customer.province}</div></div>
            </div>

            {customer.note ? (
              <div className="u-mt-16" style={{ background: "#fef3c7", borderRadius: 8, padding: 10, fontSize: 13 }}>📌 {customer.note}</div>
            ) : null}

            <div className="u-mt-16 u-flex u-wrap u-gap-4">
              {(customer.tags || []).map((tag) => (
                <Badge key={tag} tone="indigo">{tag}</Badge>
              ))}
            </div>
          </Card>

          {etax ? (
            <Card title={tx("ข้อมูลออกใบกำกับภาษี", "Tax invoice details")} actions={<Button size="sm" onClick={() => { setTaxForm(buyerTaxOf(customer)); setTaxOpen(true); }}>{tx("แก้ไข", "Edit")}</Button>}>
              <div className="u-fs-13" style={{ display: "grid", gap: 6 }}>
                <div className="u-between"><span className="u-muted">{tx("ประเภท", "Type")}</span><span>{customer.isJuristic ? tx("นิติบุคคล", "Juristic") : tx("บุคคลธรรมดา", "Individual")}</span></div>
                <div className="u-between"><span className="u-muted">{tx("เลขผู้เสียภาษี", "Tax ID")}</span><span>{customer.taxId || "-"}</span></div>
                <div className="u-between"><span className="u-muted">{tx("สาขา", "Branch")}</span><span>{customer.taxId ? customer.branchCode || "00000" : "-"}</span></div>
                <div className="u-between"><span className="u-muted">{tx("ชื่อในใบกำกับ", "Name")}</span><span>{customer.taxName || customer.name}</span></div>
                <div><span className="u-muted">{tx("ที่อยู่ในใบกำกับ", "Address")}</span><div>{customer.taxAddress || customer.address || "-"}</div></div>
              </div>
            </Card>
          ) : null}

          <Card title={tx("สรุปมูลค่าลูกค้า", "Customer value")}>
            <div className="u-between u-mb-8"><span className="u-muted u-fs-13">{tx("ยอดใช้จ่ายสะสม", "Lifetime spend")}</span><span className="u-strong">{baht(customer.totalSpend)}</span></div>
            <div className="u-between u-mb-8"><span className="u-muted u-fs-13">{tx("ยอดค้างชำระ", "Outstanding")}</span><span className="u-strong u-danger">{baht(summary?.unpaidTotal || 0)}</span></div>
            <div className="u-between u-mb-8"><span className="u-muted u-fs-13">{tx("แต้มสะสม", "Points")}</span><span className="u-strong">{customer.points}</span></div>
            <div className="u-between"><span className="u-muted u-fs-13">{tx("จำนวนครั้งที่ใช้บริการ", "Visits")}</span><span className="u-strong">{customer.visits}</span></div>
          </Card>
        </div>

        <div>
          <Tabs tabs={tabs} value={tab} onChange={setTab} />

          {tab === "overview" ? (
            <>
              <div className="grid-2">
                <Card title={tx("นัดหมายถัดไป", "Next appointment")}>
                  {summary?.nextAppointment ? (
                    <>
                      <div className="u-strong u-fs-18">{summary.nextAppointment.serviceName}</div>
                      <div className="u-muted u-fs-13">{formatDate(summary.nextAppointment.startAt, true)}</div>
                      <StatusBadge status={summary.nextAppointment.status} />
                    </>
                  ) : (
                    <EmptyState icon={<Icon name="calendarAlt" />} text={tx("ยังไม่มีนัดหมาย", "No upcoming appointment")} />
                  )}
                </Card>
                <Card title={tx("ค่างวดถัดไป", "Next installment")}>
                  {summary?.nextInstallment ? (
                    <>
                      <div className="u-strong u-fs-18">{baht(summary.nextInstallment.amount)}</div>
                      <div className="u-muted u-fs-13">{summary.nextInstallment.contractNo} · {tx("ครบกำหนด", "Due")} {formatDate(summary.nextInstallment.dueAt)}</div>
                      <StatusBadge status={summary.nextInstallment.status} />
                    </>
                  ) : (
                    <EmptyState icon={<Icon name="chartLine" />} text={tx("ไม่มีค่างวดค้าง", "No installment due")} />
                  )}
                </Card>
              </div>

              {threads?.length ? (
                <Card title={tx("บทสนทนาล่าสุด", "Recent conversations")}>
                  {threads.map((thread) => (
                    <div key={thread.id} className="u-between" style={{ padding: "8px 0", borderBottom: "1px solid var(--c-slate-100)" }}>
                      <div>
                        <Badge tone="sky">{thread.channel}</Badge> <span className="u-fs-13">{thread.lastMessage}</span>
                      </div>
                      <Link to="/admin/chat" className="u-fs-13">{tx("เปิดแชท", "Open")}</Link>
                    </div>
                  ))}
                </Card>
              ) : null}
            </>
          ) : null}

          {tab === "appointments" ? (
            <DataTable
              columns={[
                { key: "startAt", label: tx("วัน-เวลา", "Date"), render: (row) => formatDate(row.startAt, true) },
                { key: "serviceName", label: tx("บริการ", "Service") },
                { key: "price", label: tx("ราคา", "Price"), align: "right", render: (row) => baht(row.price) },
                { key: "status", label: tx("สถานะ", "Status"), render: (row) => <StatusBadge status={row.status} /> },
              ]}
              rows={summary?.appointments || []}
              emptyText={tx("ยังไม่มีประวัตินัดหมาย", "No appointments")}
            />
          ) : null}

          {tab === "billing" ? (
            <DataTable
              columns={[
                { key: "docNo", label: tx("เลขที่", "No.") },
                { key: "createdAt", label: tx("วันที่", "Date"), render: (row) => formatDate(row.createdAt) },
                { key: "total", label: tx("ยอดรวม", "Total"), align: "right", render: (row) => baht(row.total) },
                { key: "status", label: tx("สถานะ", "Status"), render: (row) => <StatusBadge status={row.status} /> },
                {
                  key: "action",
                  label: "",
                  render: (row) =>
                    row.status !== "paid" ? (
                      <Button size="sm" variant="primary" onClick={() => setPayInvoice(row)}>
                        {tx("รับชำระ", "Collect")}
                      </Button>
                    ) : null,
                },
              ]}
              rows={summary?.invoices || []}
              emptyText={tx("ยังไม่มีใบแจ้งหนี้", "No invoices")}
            />
          ) : null}

          {tab === "packages" ? (
            <div className="grid-2">
              {(summary?.packages || []).map((item) => {
                const percent = (item.usedSessions / item.totalSessions) * 100;
                return (
                  <Card key={item.id} title={item.packageName} subtitle={tx(`หมดอายุ ${formatDate(item.expireAt)}`, `Expires ${formatDate(item.expireAt)}`)}>
                    <div className="u-between u-fs-13 u-mb-8">
                      <span>{tx("ใช้ไปแล้ว", "Used")} {item.usedSessions}/{item.totalSessions}</span>
                      <span className="u-strong">{tx("เหลือ", "Left")} {item.totalSessions - item.usedSessions}</span>
                    </div>
                    <ProgressBar percent={percent} />
                  </Card>
                );
              })}
              {!summary?.packages?.length ? <EmptyState icon={<Icon name="ticket" />} text={tx("ยังไม่มีคอร์ส/แพ็กเกจ", "No packages")} /> : null}
            </div>
          ) : null}

          {tab === "contracts" ? (
            <DataTable
              columns={[
                { key: "docNo", label: tx("เลขที่", "No.") },
                { key: "title", label: tx("ชื่อสัญญา", "Title") },
                { key: "value", label: tx("มูลค่า", "Value"), align: "right", render: (row) => baht(row.value) },
                { key: "endAt", label: tx("สิ้นสุด", "End"), render: (row) => formatDate(row.endAt) },
                { key: "status", label: tx("สถานะ", "Status"), render: (row) => <StatusBadge status={row.status} /> },
              ]}
              rows={summary?.contracts || []}
              emptyText={tx("ยังไม่มีสัญญา", "No contracts")}
            />
          ) : null}

          {tab === "documents" ? (
            <div className="grid-3">
              {(documents || []).map((doc) => (
                <Card key={doc.id}>
                  <div style={{ fontSize: 30 }}><Icon name="folder" /></div>
                  <div className="u-strong u-fs-13">{doc.name}</div>
                  <div className="u-fs-12 u-muted">{doc.size} · {formatDate(doc.createdAt)}</div>
                </Card>
              ))}
              {!documents?.length ? <EmptyState icon={<Icon name="folder" />} text={tx("ยังไม่มีเอกสาร", "No documents")} /> : null}
            </div>
          ) : null}

          {tab === "activity" ? (
            <Card title={tx("ไทม์ไลน์ลูกค้า", "Customer timeline")}>
              <Timeline items={(summary?.activities || []).slice(0, 20)} />
            </Card>
          ) : null}
        </div>
      </div>

      <Modal
        open={taxOpen}
        title={tx("ข้อมูลออกใบกำกับภาษีของลูกค้า", "Customer tax details")}
        onClose={() => setTaxOpen(false)}
        footer={
          <>
            <Button onClick={() => setTaxOpen(false)}>{tx("ยกเลิก", "Cancel")}</Button>
            <Button variant="primary" onClick={saveTax}>{tx("บันทึก", "Save")}</Button>
          </>
        }
      >
        {taxForm ? <BuyerTaxFields value={taxForm} onChange={setTaxForm} /> : null}
      </Modal>

      {hasModule("billing") ? (
        <PaymentQrModal
          open={!!payInvoice}
          title={tx(`ชำระ ${payInvoice?.docNo}`, `Pay ${payInvoice?.docNo}`)}
          subtitle={customer.name}
          amount={payInvoice?.total || 0}
          orderType={ORDER_TYPE.invoice}
          reference={payInvoice?.docNo}
          items={[{ productName: `${payInvoice?.docNo}`, quantity: 1, price: payInvoice?.total || 0, detail: { customerId: customer.id } }]}
          orgId={orgId}
          onClose={() => setPayInvoice(null)}
          onPaid={async (qr) => {
            // ปิดบิล + ออกใบเสร็จ (กันซ้ำด้วย transactionId) — เดิมแค่แจ้งเตือนโดยไม่อัปเดตใบแจ้งหนี้
            await settleInvoicePayment({ orgId, invoiceId: payInvoice.id, transactionId: qr.transactionId, amount: qr.amount, refNo: qr.refNo, organization });
            toast(tx("รับชำระเงินเรียบร้อย", "Payment received"), "success");
            reload();
          }}
        />
      ) : null}
    </>
  );
}
